import { Helmet } from 'react-helmet-async';
import react, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Radio,
  Switch,
} from '@mui/material';
import { OrganizationOverview, NewOrganizationView } from '../components/organization';
// @mui
// components
import useAuth from '../hooks/useAuth';
import { getOrganization } from '../services/organizationService';
import { updateSettings } from '../services/userService';
import { infoNotification, errorNotification } from '../services/notificationService';

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser, logout } = useAuth();
  const [displayTemperatureAsFahrenheit, setDisplayTemperatureAsFahrenheit] = useState(
    user?.settings?.playback?.displayTemperatureAsFahrenheit || false
  );

  const [isLoading, setIsLoading] = useState(true);

  const [organization, setOrganization] = useState(null);
  const [isLoadingOrganization, setIsLoadingOrganization] = useState(false);
  
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        const userData = await refreshUser();
        setDisplayTemperatureAsFahrenheit(userData?.settings?.playback?.displayTemperatureAsFahrenheit || false);
        if (userData?.organizationId) {
          setIsLoadingOrganization(true);
          try {
            const organizationData = await getOrganization(userData.organizationId);
            setOrganization(organizationData);
          } catch (orgError) {
            console.error('Error loading organization:', orgError);
          } finally {
            setIsLoadingOrganization(false);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        errorNotification(error.message || 'Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadUserData();
    } else {
      setIsLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    const displayTemperatureAsFahrenheit = e;
    setDisplayTemperatureAsFahrenheit(displayTemperatureAsFahrenheit);
    if (user) {
      const updatedUser = {
        ...user,
        settings: {
          ...user.settings,
          playback: {
            ...user.settings?.playback,
            displayTemperatureAsFahrenheit,
          },
        },
      };
      updateUser(updatedUser);
    }
  }

  function handleSave() {
    if (!user) return;
    updateSettings(user.settings)
      .then(() => {
        infoNotification('Settings saved');
        refreshUser();
      })
      .catch((error) => {
        errorNotification(error.response?.data?.message || 'Failed to save settings');
      });
  }

  function onLogoutClick() {
    logout();
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title> VPSI | Account </title>
      </Helmet>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              Account
            </Typography>
          </Stack>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Information" />
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <strong>Username:</strong> {user.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <strong>Email:</strong> {user.email}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Settings" />
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Manage your account settings and set email preferences.
                  </Typography>
                  <FormControl sx={{ my: 2 }} focused={false}>
                    <FormLabel>Temperature Scale</FormLabel>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={displayTemperatureAsFahrenheit}
                          onChange={(e) => handleChange(e.target.checked)}
                        />
                      }
                      label="Display temperature in Fahrenheit"
                    />
                  </FormControl>
                  <br />
                  <br />
                  <br />
                  <Container disableGutters>
                    <Button variant="contained" color="primary" onClick={() => handleSave()}>
                      Save
                    </Button>
                  </Container>
                </CardContent>
              </Card>
            </Grid>
            {organization && !isLoadingOrganization && (
              <Grid item xs={12} sm={12} md={12}>
                <OrganizationOverview organization={organization} />
              </Grid>
            )}
            {!organization && !isLoadingOrganization && (
              <Grid item xs={12} sm={12} md={12}>
                <NewOrganizationView />
              </Grid>
            )}
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Actions" />
                <CardContent>
                  <br />
                  <Button variant="contained" color="primary" fullWidth onClick={() => onLogoutClick()}>
                    Logout
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
}
